'use client'

/**
 * Agency Analytics Hooks
 *
 * React Query hooks for fetching agency dashboard analytics data.
 * Each hook fetches one section independently, enabling parallel loading
 * and independent Suspense boundaries.
 */
import { useMemo } from 'react'

import { queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import {
  fetchAgencyAwardTypes,
  fetchAgencyGeography,
  fetchAgencySetAsides,
  fetchAgencyTimeSeries,
  fetchAgencyTopPscCodes,
} from '@/context/usaspending'

import { assertToken } from './utils'

/**
 * Agency analytics data can use longer stale time - 30 min
 */
const ANALYTICS_STALE_TIME = 30 * 60 * 1000

/**
 * Cache for 1 hour
 */
const ANALYTICS_GC_TIME = 60 * 60 * 1000

// =============================================================================
// Query Keys
// =============================================================================

export const agencyAnalyticsKeys = {
  all: ['usaspending', 'agency', 'analytics'] as const,
  timeSeries: (code: string) =>
    [...agencyAnalyticsKeys.all, 'time-series', code] as const,
  awardTypes: (code: string, fiscalYear?: number) =>
    [...agencyAnalyticsKeys.all, 'award-types', code, { fiscalYear }] as const,
  geography: (code: string, fiscalYear?: number) =>
    [...agencyAnalyticsKeys.all, 'geography', code, { fiscalYear }] as const,
  setAsides: (code: string, fiscalYear?: number) =>
    [...agencyAnalyticsKeys.all, 'set-asides', code, { fiscalYear }] as const,
  topPscCodes: (code: string, fiscalYear?: number) =>
    [...agencyAnalyticsKeys.all, 'psc-codes', code, { fiscalYear }] as const,
}

// =============================================================================
// Query Options Factory
// =============================================================================

/**
 * Factory hook for agency analytics query options.
 * Use these for prefetching, SSR, or manual query management.
 */
export function useAgencyAnalyticsQueries() {
  const { accessToken } = useAuthInfo()

  return useMemo(
    () => ({
      timeSeries: (agencyCode: string) =>
        queryOptions({
          queryKey: agencyAnalyticsKeys.timeSeries(agencyCode),
          queryFn: () =>
            fetchAgencyTimeSeries(assertToken(accessToken), agencyCode),
          staleTime: ANALYTICS_STALE_TIME,
          gcTime: ANALYTICS_GC_TIME,
          refetchOnWindowFocus: false,
          enabled: !!accessToken,
        }),

      awardTypes: (agencyCode: string, fiscalYear?: number) =>
        queryOptions({
          queryKey: agencyAnalyticsKeys.awardTypes(agencyCode, fiscalYear),
          queryFn: () =>
            fetchAgencyAwardTypes(
              assertToken(accessToken),
              agencyCode,
              fiscalYear,
            ),
          staleTime: ANALYTICS_STALE_TIME,
          gcTime: ANALYTICS_GC_TIME,
          refetchOnWindowFocus: false,
          enabled: !!accessToken,
        }),

      geography: (agencyCode: string, fiscalYear?: number) =>
        queryOptions({
          queryKey: agencyAnalyticsKeys.geography(agencyCode, fiscalYear),
          queryFn: () =>
            fetchAgencyGeography(
              assertToken(accessToken),
              agencyCode,
              fiscalYear,
            ),
          staleTime: ANALYTICS_STALE_TIME,
          gcTime: ANALYTICS_GC_TIME,
          refetchOnWindowFocus: false,
          enabled: !!accessToken,
        }),

      setAsides: (agencyCode: string, fiscalYear?: number) =>
        queryOptions({
          queryKey: agencyAnalyticsKeys.setAsides(agencyCode, fiscalYear),
          queryFn: () =>
            fetchAgencySetAsides(
              assertToken(accessToken),
              agencyCode,
              fiscalYear,
            ),
          staleTime: ANALYTICS_STALE_TIME,
          gcTime: ANALYTICS_GC_TIME,
          refetchOnWindowFocus: false,
          enabled: !!accessToken,
        }),

      topPscCodes: (agencyCode: string, fiscalYear?: number) =>
        queryOptions({
          queryKey: agencyAnalyticsKeys.topPscCodes(agencyCode, fiscalYear),
          queryFn: () =>
            fetchAgencyTopPscCodes(
              assertToken(accessToken),
              agencyCode,
              fiscalYear,
            ),
          staleTime: ANALYTICS_STALE_TIME,
          gcTime: ANALYTICS_GC_TIME,
          refetchOnWindowFocus: false,
          enabled: !!accessToken,
        }),
    }),
    [accessToken],
  )
}

// =============================================================================
// Standard Hooks (useQuery)
// =============================================================================

/**
 * Fetch budget/spending time series for an agency.
 * Shows obligation trend by fiscal year (FY2023+).
 */
export function useAgencyTimeSeries(agencyCode: string) {
  const queries = useAgencyAnalyticsQueries()
  return useQuery(queries.timeSeries(agencyCode))
}

/**
 * Fetch award type breakdown for an agency.
 * Shows distribution across contract, grant, IDV, loan, etc.
 */
export function useAgencyAwardTypes(agencyCode: string, fiscalYear?: number) {
  const queries = useAgencyAnalyticsQueries()
  return useQuery(queries.awardTypes(agencyCode, fiscalYear))
}

/**
 * Fetch geographic distribution for an agency.
 * Shows spending by place of performance state.
 */
export function useAgencyGeography(agencyCode: string, fiscalYear?: number) {
  const queries = useAgencyAnalyticsQueries()
  return useQuery(queries.geography(agencyCode, fiscalYear))
}

/**
 * Fetch set-aside utilization for an agency.
 * Shows distribution by set-aside program type.
 */
export function useAgencySetAsides(agencyCode: string, fiscalYear?: number) {
  const queries = useAgencyAnalyticsQueries()
  return useQuery(queries.setAsides(agencyCode, fiscalYear))
}

/**
 * Fetch top PSC codes for an agency.
 * Shows top product/service codes by spending.
 */
export function useAgencyTopPscCodes(agencyCode: string, fiscalYear?: number) {
  const queries = useAgencyAnalyticsQueries()
  return useQuery(queries.topPscCodes(agencyCode, fiscalYear))
}

// =============================================================================
// Suspense Hooks (useSuspenseQuery)
// =============================================================================

/**
 * Suspense-compatible time series hook.
 * Use within a Suspense boundary for streaming loading.
 */
export function useSuspenseAgencyTimeSeries(agencyCode: string) {
  const queries = useAgencyAnalyticsQueries()
  return useSuspenseQuery(queries.timeSeries(agencyCode))
}

/**
 * Suspense-compatible award types hook.
 */
export function useSuspenseAgencyAwardTypes(
  agencyCode: string,
  fiscalYear?: number,
) {
  const queries = useAgencyAnalyticsQueries()
  return useSuspenseQuery(queries.awardTypes(agencyCode, fiscalYear))
}

/**
 * Suspense-compatible geography hook.
 */
export function useSuspenseAgencyGeography(
  agencyCode: string,
  fiscalYear?: number,
) {
  const queries = useAgencyAnalyticsQueries()
  return useSuspenseQuery(queries.geography(agencyCode, fiscalYear))
}

/**
 * Suspense-compatible set-asides hook.
 */
export function useSuspenseAgencySetAsides(
  agencyCode: string,
  fiscalYear?: number,
) {
  const queries = useAgencyAnalyticsQueries()
  return useSuspenseQuery(queries.setAsides(agencyCode, fiscalYear))
}

/**
 * Suspense-compatible top PSC codes hook.
 */
export function useSuspenseAgencyTopPscCodes(
  agencyCode: string,
  fiscalYear?: number,
) {
  const queries = useAgencyAnalyticsQueries()
  return useSuspenseQuery(queries.topPscCodes(agencyCode, fiscalYear))
}
